import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { FragnoButton } from 'fragno/react';
import useOtpInput from '../../../hooks/useOtpInput';
import useCountdownTimer from '../../../hooks/useCountdownTimer';
import WalletAlert from '../WalletAlert';
import style from '../../../style/components/profile/emails/InlineOtpPanel.module.scss';

const OTP_EXPIRY_SECONDS = 600; // 10 minutes
const RESEND_COOLDOWN_SECONDS = 60;

const InlineOtpPanel = ({
  emailAddress,
  otpError,
  onCancel,
  onResend,
  onComplete,
  verificationId,
  onPoll,
  onExpire,
}) => {
  const { t } = useTranslation();
  const [isVerifying, setIsVerifying] = useState(false);
  const {
    secondsRemaining,
    isExpired,
    reset: resetTimer,
  } = useCountdownTimer(OTP_EXPIRY_SECONDS);
  // Start the cooldown active on mount — the original `initiate` already
  // counted as a send for the platform's 30s rate limit, so an immediate
  // resend would 429. Our 60s cooldown is the conservative side of that.
  const { isExpired: cooldownReady, reset: startCooldown } = useCountdownTimer(
    RESEND_COOLDOWN_SECONDS,
  );

  // Notify the parent exactly once when the client-side countdown expires,
  // so the funnel can record a "timeout" abandonment. The timer only counts
  // down (never resets to expired), and a resend calls resetTimer() which
  // flips isExpired back to false — this ref guards against a repeat emit if
  // isExpired transitions true again on a subsequent cycle.
  const expireReportedRef = useRef(false);
  useEffect(() => {
    if (isExpired && !expireReportedRef.current) {
      expireReportedRef.current = true;
      if (onExpire) onExpire();
    }
    if (!isExpired) {
      expireReportedRef.current = false;
    }
  }, [isExpired, onExpire]);

  // Handle OTP completion — delegate to parent via onComplete.
  // Set isVerifying to disable inputs while Redux processes the action.
  const handleOtpComplete = useCallback(
    (fullCode) => {
      setIsVerifying(true);
      if (onComplete) {
        onComplete(fullCode);
      }
    },
    [onComplete],
  );

  const {
    code,
    inputRefs,
    handleChange,
    handleKeyDown,
    handlePaste,
    resetCode,
  } = useOtpInput(handleOtpComplete);

  // Reset verifying state, clear inputs, and focus first input on OTP error.
  // role="alert" on the error message announces it to screen readers;
  // focus moves to first input so user can retype immediately.
  useEffect(() => {
    if (otpError) {
      setIsVerifying(false);
      resetCode();
      // Delay focus until after React re-renders the cleared inputs
      requestAnimationFrame(() => {
        inputRefs.current[0]?.focus();
      });
    }
  }, [otpError, resetCode, inputRefs]);

  // Autofocus the first digit input on mount. The heading and subtext are
  // referenced via aria-describedby on the fieldset, so screen readers will
  // announce them alongside the input's label.
  useEffect(() => {
    requestAnimationFrame(() => {
      inputRefs.current[0]?.focus();
    });
  }, [inputRefs]);

  // Hoisted so handleResendClick can guard against firing on a locked-out
  // panel even if the button's disabled prop is bypassed (keyboard activation,
  // future refactor that drops the prop, etc.).
  const isResendLocked = otpError?.errorCode === 'RESEND_LIMIT';

  const handleResendClick = useCallback(() => {
    if (isResendLocked) return;
    if (!cooldownReady) return;
    resetTimer();
    resetCode();
    startCooldown();
    if (onResend) {
      onResend();
    }
    requestAnimationFrame(() => {
      inputRefs.current[0]?.focus();
    });
  }, [
    isResendLocked,
    cooldownReady,
    onResend,
    resetCode,
    resetTimer,
    startCooldown,
    inputRefs,
  ]);

  const handleCancelClick = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const showWarningIcon = secondsRemaining <= 60 && secondsRemaining > 0;

  // Out of verification attempts: server returned INVALID_TOKEN with remainingAttempts: 0.
  // Treated as its own lockout state — user must resend to get a fresh code.
  const isOutOfAttempts =
    otpError?.errorCode === 'INVALID_TOKEN' &&
    otpError?.remainingAttempts === 0;
  // Terminal lockout states: RESEND_LIMIT (too many resend attempts) or CODE_EXPIRED (server).
  const isTerminalLockout =
    otpError?.errorCode === 'RESEND_LIMIT' ||
    otpError?.errorCode === 'CODE_EXPIRED';
  const isLockedOut = isOutOfAttempts || isExpired || isTerminalLockout;

  const renderErrorText = () => {
    if (!otpError) return null;
    if (
      otpError.errorCode === 'INVALID_TOKEN' &&
      typeof otpError.remainingAttempts === 'number'
    ) {
      return t('walletEmails.otpInvalidCodeWithAttempts', {
        count: otpError.remainingAttempts,
      });
    }
    if (otpError.errorCode === 'INVALID_TOKEN') {
      return t('walletEmails.otpInvalidCode');
    }
    if (otpError.source === 'resend') {
      return t('walletEmails.otpResendError');
    }
    return t('walletEmails.otpVerifyError');
  };

  // Cross-device polling (AXP-1251): poll verification status every 3s while
  // the panel is active. Stops on unmount, lockout, expiry, or when the user
  // is mid-verify. The cleanup runs on the next render after the deps flip,
  // which leaves a 1-2 frame window where a tick can fire concurrently with
  // the verifyOtp dispatch. The reducer is idempotent on VERIFY_OTP_SUCCESS,
  // so a duplicate dispatch is harmless. The AbortController also cancels
  // any in-flight request before it can resolve into a stale dispatch.
  useEffect(() => {
    if (!verificationId || isLockedOut || isVerifying || isExpired) return;

    const controller = new AbortController();
    const intervalId = setInterval(() => {
      if (onPoll) onPoll(controller.signal);
    }, 3000);

    return () => {
      clearInterval(intervalId);
      controller.abort();
    };
  }, [verificationId, onPoll, isLockedOut, isVerifying, isExpired]);

  const resendDisabled = isVerifying || !cooldownReady || isResendLocked;
  // When locked out, the button is permanently disabled — show the static
  // label rather than the active cooldown countdown.
  const resendButtonLabel =
    isResendLocked || cooldownReady
      ? t('walletEmails.otpResendButton')
      : t('walletEmails.otpResendCooldownStatic');

  return (
    <div className={style.panel}>
      <h3 className={style.heading} id="otp-heading">
        {t('walletEmails.otpHeading')}
      </h3>
      <p className={style.subtext} id="otp-subtext">
        <Trans
          i18nKey="walletEmails.otpSubtext"
          values={{ email: emailAddress }}
          components={{ bold: <strong /> }}
        />
      </p>

      {!isLockedOut ? (
        <>
          <fieldset
            className={style.codeInputContainer}
            aria-describedby="otp-heading otp-subtext"
          >
            <legend className="sr-only">{t('walletEmails.otpHeading')}</legend>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                autoComplete="one-time-code"
                className={`${style.codeInput} ${
                  otpError ? style.codeInputError : ''
                }`}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={isVerifying || isExpired}
                aria-label={t('walletEmails.otpDigit', { number: index + 1 })}
                aria-invalid={!!otpError}
              />
            ))}
          </fieldset>

          {otpError && (
            <p className={style.otpError} role="alert">
              <span className={style.otpErrorIcon} aria-hidden="true">
                <idme-icon name="alert-circle" size="small" />
              </span>
              <span>{renderErrorText()}</span>
            </p>
          )}

          <div className={style.timerContainer}>
            {showWarningIcon && (
              <span className={style.timerIcon}>
                <idme-icon name="alert-triangle" size="small" />
              </span>
            )}
            <span className={style.timerText}>
              {showWarningIcon
                ? t('walletEmails.otpExpiresWarningStatic')
                : t('walletEmails.otpExpiresStatic')}
            </span>
          </div>

          <p className={style.resendText}>
            {cooldownReady ? (
              <>
                {t('walletEmails.otpResendPrompt')}{' '}
                <button
                  type="button"
                  className={style.resendLink}
                  onClick={handleResendClick}
                  disabled={isVerifying}
                  aria-label={t('walletEmails.otpResendButton')}
                >
                  {t('walletEmails.otpResendLinkText')}{' '}
                  <span
                    aria-hidden="true"
                    style={{ display: 'inline-block', textDecoration: 'none' }}
                  >
                    ↻
                  </span>
                </button>
              </>
            ) : (
              t('walletEmails.otpResendCooldownStatic')
            )}
          </p>

          <div className={style.cancelRow}>
            <FragnoButton
              hierarchy="secondary"
              label={t('walletEmails.otpCancel')}
              onClick={handleCancelClick}
              size="md"
            />
          </div>
        </>
      ) : (
        <>
          <div className={style.expiredAlert} role="alert">
            <WalletAlert variant="error">
              <strong>
                {isOutOfAttempts
                  ? t('walletEmails.otpNoAttemptsMessage')
                  : otpError?.errorCode === 'RESEND_LIMIT'
                  ? t('walletEmails.otpResendLimitMessage')
                  : t('walletEmails.otpExpiredMessage')}
              </strong>
              <br />
              {isOutOfAttempts
                ? t('walletEmails.otpNoAttemptsBody')
                : otpError?.errorCode === 'RESEND_LIMIT'
                ? t('walletEmails.otpResendLimitBody')
                : t('walletEmails.otpExpiredBody')}
            </WalletAlert>
          </div>

          <div className={style.buttonGroup}>
            <FragnoButton
              hierarchy="secondary"
              label={t('walletEmails.otpCancel')}
              onClick={handleCancelClick}
            />
            <FragnoButton
              hierarchy="primary"
              label={resendButtonLabel}
              onClick={handleResendClick}
              disabled={resendDisabled}
            />
          </div>
        </>
      )}

      <button
        className={style.closeButton}
        onClick={handleCancelClick}
        aria-label={t('walletEmails.otpCancel')}
        type="button"
      >
        ✕
      </button>
    </div>
  );
};

InlineOtpPanel.propTypes = {
  emailAddress: PropTypes.string.isRequired,
  otpError: PropTypes.shape({
    errorCode: PropTypes.string,
    error: PropTypes.string,
    remainingAttempts: PropTypes.number,
    source: PropTypes.string,
  }),
  onCancel: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  verificationId: PropTypes.string,
  onPoll: PropTypes.func,
  onExpire: PropTypes.func,
};

export default InlineOtpPanel;
